import React, { useState, useEffect, useRef } from 'react';
import ReactMapBoxGl, { Marker, GeoJSONLayer } from 'react-mapbox-gl';

import './style.scss';
import {
  resolveBirthLocationAsync,
  resolveCountryByLabel,
  resolveCountryFromBirthText,
} from './countryData';
import { getPrismicSizedImageUrl, getPrismicStructuredTextPlain } from '../../utils/functions';

const Map = ReactMapBoxGl({
  accessToken: process.env.GATSBY_MAPBOX_API_KEY,
  scrollZoom: false,
  minZoom: 1.5,
  maxZoom: 11,
});

const BIRTH_ZOOM = 6.8;
const OVERVIEW_ZOOM = 2.5;
const EASE_DURATION_MS = 1200;

const offSetValues = [[0, 0], [-55, 5], [5, -55], [0, 30], [55, 0], [55, 25], [-25, -55]];

const getPlacesFromFamilyOrigin = (familyOrigin) =>
  (familyOrigin || [])
    .map((origin) => resolveCountryByLabel(origin.country?.[0]?.text))
    .filter(Boolean);

const dedupePlacesByCoordinates = (places) =>
  places.filter(
    (place, index, list) =>
      list.findIndex(
        (other) =>
          other.coordinates[0] === place.coordinates[0] && other.coordinates[1] === place.coordinates[1]
      ) === index
  );

const getBoundsFromPlaces = (places) => {
  const coords = places.map((place) => place.coordinates).filter(Boolean);
  if (coords.length < 2) return null;

  const lngs = coords.map(([lng]) => lng);
  const lats = coords.map(([, lat]) => lat);

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

/** More family origins → lower max zoom so every country stays in frame */
const getFamilyDetailMaxZoom = (placeCount) => {
  if (placeCount >= 4) return 3.5;
  if (placeCount === 3) return 4.2;
  if (placeCount === 2) return 5;
  return BIRTH_ZOOM;
};

const getFamilyBoundsPadding = (placeCount) => ({
  top: 56,
  bottom: 148,
  left: 56 + placeCount * 12,
  right: 56 + placeCount * 12,
});

const getMarkers = (element, coordinates, markerKey, offset = 0, compact = false) => {
  const elementData = element?.data;
  const offsetIndex = Math.min(offset, offSetValues.length - 1);

  return (
    <Marker
      coordinates={coordinates}
      offset={offSetValues[offsetIndex]}
      anchor="center"
      key={markerKey}
    >
      <div
        className={`map-marker-individual map-marker-individual--portrait${
          compact ? ' map-marker-individual--compact' : ''
        }`}
      >
        <img
          src={getPrismicSizedImageUrl(elementData?.portraitPhoto?.url, compact ? 160 : 320)}
          alt=""
        />
      </div>
    </Marker>
  );
};

const centerCamera = (center, zoom) => ({
  mode: 'center',
  center,
  zoom,
  fitBounds: undefined,
  fitBoundsOptions: undefined,
});

const boundsCamera = (fitBounds, maxZoom, padding = 72) => {
  const paddingValue =
    typeof padding === 'object' ? padding : { top: padding, bottom: padding, left: padding, right: padding };

  return {
    mode: 'bounds',
    center: undefined,
    zoom: undefined,
    fitBounds,
    fitBoundsOptions: {
      padding: paddingValue,
      maxZoom,
      duration: EASE_DURATION_MS,
      linear: false,
    },
  };
};

const MapComponent = ({ member = {}, showBy = '' }) => {
  const memberData = member?.data;
  const [placesToMap, setPlacesToMap] = useState([]);
  const [camera, setCamera] = useState(centerCamera([0, 0], OVERVIEW_ZOOM));
  const zoomInTimerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (zoomInTimerRef.current) {
      clearTimeout(zoomInTimerRef.current);
      zoomInTimerRef.current = null;
    }

    const runTwoStepCenter = (overviewCenter, detailCenter, detailZoom) => {
      setCamera(centerCamera(overviewCenter, OVERVIEW_ZOOM));
      zoomInTimerRef.current = setTimeout(() => {
        setCamera(centerCamera(detailCenter, detailZoom));
      }, EASE_DURATION_MS + 80);
    };

    const runTwoStepBounds = (places) => {
      const bounds = getBoundsFromPlaces(places);
      if (!bounds) return false;

      const placeCount = places.length;
      const padding = getFamilyBoundsPadding(placeCount);
      const detailMaxZoom = getFamilyDetailMaxZoom(placeCount);
      const overviewMaxZoom = Math.min(OVERVIEW_ZOOM, detailMaxZoom - 0.8);

      setCamera(boundsCamera(bounds, overviewMaxZoom, padding));
      zoomInTimerRef.current = setTimeout(() => {
        setCamera(boundsCamera(bounds, detailMaxZoom, padding));
      }, EASE_DURATION_MS + 80);
      return true;
    };

    if (showBy === 'familyOrigin') {
      const places = dedupePlacesByCoordinates(getPlacesFromFamilyOrigin(memberData?.familyOrigin));
      setPlacesToMap(places);

      if (places.length >= 2) {
        if (!runTwoStepBounds(places)) {
          setPlacesToMap([]);
        }
      } else if (places.length === 1) {
        const target = places[0].coordinates;
        runTwoStepCenter(target, target, BIRTH_ZOOM);
      } else {
        setPlacesToMap([]);
      }

      return () => {
        if (zoomInTimerRef.current) {
          clearTimeout(zoomInTimerRef.current);
        }
      };
    }

    let cancelled = false;

    if (showBy === 'birthCountry') {
      const birthText = getPrismicStructuredTextPlain(memberData?.birthCountry);

      const applyBirthPlace = (resolved) => {
        if (cancelled || !resolved) return;
        setPlacesToMap([resolved]);
        const country = resolveCountryFromBirthText(birthText);
        const detailZoom = resolved.birthZoom ?? BIRTH_ZOOM;

        if (country) {
          runTwoStepCenter(country.coordinates, resolved.coordinates, detailZoom);
        } else {
          setCamera(centerCamera(resolved.coordinates, detailZoom));
        }
      };

      resolveBirthLocationAsync(birthText).then((resolved) => {
        if (!resolved) {
          if (!cancelled) setPlacesToMap([]);
          return;
        }
        applyBirthPlace(resolved);
      });
    }

    return () => {
      cancelled = true;
      if (zoomInTimerRef.current) {
        clearTimeout(zoomInTimerRef.current);
      }
    };
  }, [showBy, member, memberData]);

  return (
    <>
      <link href="https://api.mapbox.com/mapbox-gl-js/v1.10.1/mapbox-gl.css" rel="stylesheet" />
      <Map
        style="mapbox://styles/lineagejourney/ckhz34cwa0xux1alun7bzqmq7"
        containerStyle={{
          height: '100%',
          width: '100%',
        }}
        center={camera.mode === 'center' ? camera.center : undefined}
        zoom={camera.mode === 'center' ? [camera.zoom] : undefined}
        fitBounds={camera.mode === 'bounds' ? camera.fitBounds : undefined}
        fitBoundsOptions={camera.mode === 'bounds' ? camera.fitBoundsOptions : undefined}
        movingMethod="easeTo"
        animationOptions={{ duration: EASE_DURATION_MS }}
        ref={mapRef}
      >
        {placesToMap.map((place, index) =>
          getMarkers(
            member,
            place.coordinates,
            `place-marker-${index}`,
            index,
            placesToMap.length >= 2
          )
        )}
        {placesToMap.map((places, index) => (
          <GeoJSONLayer
            data={places.tileset}
            layerId={`${places.coordinates[0]}-${index}`}
            id={`${places.coordinates[0]}-${index}`}
            key={`geoLayer-${places.coordinates[0]}-${index}`}
            fillPaint={{
              'fill-color': '#FFDB4D',
              'fill-opacity': 0.6,
            }}
          />
        ))}
      </Map>
    </>
  );
};

export default MapComponent;
